import React, { useState, useEffect,useRef } from "react";
import axios from "axios";
import { Table, Button } from "antd";
import * as XLSX from "sheetjs-style";
import moment from "moment";
import { Link } from "react-router-dom";


const PayrollHistory = () => {
  const [data, setData] = useState([]);
  const isInitialMount = useRef(true); // To ensure API call happens only once on mount

  const getYearRange = (year) => {
    const start = moment(year).month(3).startOf("month"); // April
    const end = moment(year).add(1, "year").month(2).endOf("month"); // March next year
    
    return { fromDate: start.format("YYYY-MM-DD"), toDate: end.format("YYYY-MM-DD") };
  };
  const [loading, setLoading] = useState(false);
  const [currentYear, setCurrentYear] = useState(moment());
  const [yearRange, setYearRange] = useState(getYearRange(moment()));

  // Define getYearRange function before useState


const maximizeBtnRef = useRef(null);


useEffect(() => {
  if (isInitialMount.current) {
    // Ensure API is called only on initial mount
    fetchPayrollHistory(yearRange.fromDate, yearRange.toDate);
    isInitialMount.current = false;
  }
}, []); // Empty dependency array ensures this runs only once on mount

useEffect(() => {
  if (!isInitialMount.current) {
    // Fetch new data when yearRange changes (after initial mount)
    fetchPayrollHistory(yearRange.fromDate, yearRange.toDate);
  }
}, [yearRange]);

  const fetchPayrollHistory = async (fromDate, toDate) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payroll/payroll-history`,
        { from_date: fromDate, to_date: toDate },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setData(response.data.history || []);
    } catch (error) {
      console.error("Error fetching payroll history:", error);
    } finally {
      setLoading(false);
    }
  };

 

  const handlePrevYear = () => {
    const prevYear = currentYear.clone().subtract(1, "year");
    setCurrentYear(prevYear);
    setYearRange(getYearRange(prevYear));
  };

  const handleNextYear = () => {
    const nextYear = currentYear.clone().add(1, "year");
    setCurrentYear(nextYear);
    setYearRange(getYearRange(nextYear));
  };

  const handleExportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    const companyName = "XYZ Company Name";
    const yearRangeText = `${yearRange.fromDate} to ${yearRange.toDate}`;
    const sheetHeading = `Payroll History Report (${yearRangeText})`;

    const headerData = [
      [`${companyName}`],
      [`${sheetHeading}`],
      [
        "Payroll Period",
        "Total Employees Paid",
        "Total Salary",
        "Total Deductions",
        "Amount Disbursed",
        "Status",
        "Date Processed",
      ],
    ];

    const tableBody = data.map((record) => [
      `${new Date(record.payroll_start_date).toLocaleString("default", {
        month: "long",
      })} ${new Date(record.payroll_start_date).getFullYear()}`,
      record.total_employees,
      `₹${record.total_salary.toLocaleString("en-IN")}`,
      `₹${record.total_deductions.toLocaleString("en-IN")}`,
      `₹${(record.total_salary - record.total_deductions).toLocaleString("en-IN")}`,
      record.payroll_status,
      record.payment_date || "N/A",
    ]);

    const totalEmployees = data.reduce((sum, record) => sum + (record.total_employees || 0), 0);
    const totalSalary = data.reduce((sum, record) => sum + (record.total_salary || 0), 0);
    const totalDeductions = data.reduce((sum, record) => sum + (record.total_deductions || 0), 0);
    const totalDisbursed = data.reduce(
      (sum, record) => sum + ((record.total_salary || 0) - (record.total_deductions || 0)),
      0
    );

    tableBody.push([]);
    tableBody.push([
      "TOTAL",
      totalEmployees,
      `₹${totalSalary.toLocaleString("en-IN")}`,
      `₹${totalDeductions.toLocaleString("en-IN")}`,
      `₹${totalDisbursed.toLocaleString("en-IN")}`,
      "",
      "",
    ]);

    const sheetData = [...headerData, ...tableBody];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
    ];

    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 20 },
    ];

    Object.keys(worksheet).forEach((cellAddress) => {
      if (!cellAddress.startsWith("!")) {
        worksheet[cellAddress].s = {
          font: { name: "Arial", sz: 10, bold: false },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
        };
      }
    });

    worksheet["A1"].s = {
      font: { bold: true, sz: 18 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    worksheet["A2"].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    headerData[2].forEach((_, index) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 2, c: index });
      worksheet[cellAddress].s = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      };
    });

    XLSX.utils.book_append_sheet(workbook, worksheet, "Payroll History");
    XLSX.writeFile(workbook, `Payroll_History_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const columns = [
    {
      title: "Payroll Period",
      dataIndex: "payroll_start_date",
      key: "payroll_period",
      render: (text, record) =>
        `${new Date(record.payroll_start_date).toLocaleString("default", {
          month: "long",
        })} ${new Date(record.payroll_start_date).getFullYear()}`,
    },
    { title: "Total Employees Paid", dataIndex: "total_employees", key: "total_employees" },
    {
      title: "Total Salary",
      dataIndex: "total_salary",
      key: "total_salary",
      render: (value) => `₹${value.toLocaleString("en-IN")}`,
    },
    {
      title: "Total Deductions",
      dataIndex: "total_deductions",
      key: "total_deductions",
      render: (value) => `₹${value.toLocaleString("en-IN")}`,
    },
    {
      title: "Amount Disbursed",
      key: "amount_disbursed",
      render: (text, record) =>
        `₹${(record.total_salary - record.total_deductions).toLocaleString("en-IN")}`,
    },
    { title: "Status", dataIndex: "payroll_status", key: "payroll_status" },
    { title: "Date Processed", dataIndex: "payment_date", key: "payment_date" },
  ];

  return (
<div className="page-wrapper">
  <div className="content container-fluid">
    <div className="page-header page-top-icons">
      <div className="row align-items-center justify-content-between">
        {/* Title */}
        <div className="col-auto">
          <h3 className="page-title">Payroll History</h3>
        </div>

        {/* Year Navigation */}
        <div className="col-auto text-center year-switcher">
          <Button className="fa fa-chevron-circle-left" onClick={handlePrevYear} />
          <Button style={{ margin: '0 1px' }}>
            <span style={{ margin: '0 1px', fontWeight: 'bold' }}>
              {moment(yearRange.fromDate).format("MMM YYYY")} - {moment(yearRange.toDate).format("MMM YYYY")}
            </span>
          </Button>
          <Button className="fa fa-chevron-circle-right" onClick={handleNextYear} />
        </div>

        {/* Maximize and Export */}
        <div className="col-auto text-end">
          <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
            <i className="las la-expand-arrows-alt" />
          </Link>
          <Button type="primary" onClick={handleExportToExcel} style={{ marginLeft: "10px" }}>
            Export to Excel
          </Button>
        </div>
      </div>
    </div>

    {/* Table */}
    <Table
      dataSource={data}
      columns={columns}
      rowKey="payroll_id"
      loading={loading}
      pagination={{ pageSize: 10 }}
      summary={(pageData) => {
        let totalEmployees = 0;
        let totalSalary = 0;
        let totalDeductions = 0;
        let totalDisbursed = 0;

        pageData.forEach(({ total_employees, total_salary, total_deductions }) => {
          totalEmployees += total_employees || 0;
          totalSalary += total_salary || 0;
          totalDeductions += total_deductions || 0;
          totalDisbursed += (total_salary - total_deductions) || 0;
        });

        return (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>Totals</Table.Summary.Cell>
            <Table.Summary.Cell index={1}>{totalEmployees}</Table.Summary.Cell>
            <Table.Summary.Cell index={2}>
              ₹{totalSalary.toLocaleString("en-IN")}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              ₹{totalDeductions.toLocaleString("en-IN")}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4}>
              ₹{totalDisbursed.toLocaleString("en-IN")}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={5}></Table.Summary.Cell>
            <Table.Summary.Cell index={6}></Table.Summary.Cell>
          </Table.Summary.Row>
        );
      }}
    />
  </div>
</div>

  
  );
};

export default PayrollHistory;
